<template>
  <div
    :class="[$style.subtleShadow, 'block relative w-full overflow-hidden rounded-lg']"
    v-on="
      isTouchDevice ? { click: handleClick } : { mouseenter: handleEnter, mouseleave: handleLeave }
    "
  >
    <Block :block="block.innerBlocks[0]" :lazy="lazy" class="w-full h-full object-cover" />
    <div
      class="absolute inset-0 bg-white flex flex-col justify-between px-ds-md py-ds-sm md:py-ds-xxs lg:py-ds-sm"
      :class="[$style.cardContent, { [$style.hovered]: hover }]"
    >
      <div class="flex justify-between items-center mb-ds-sm md:mb-0 lg:mb-ds-sm">
        <p
          class="text-rebrandSm md:text-base lg:text-rebrandSm leading-1 lg:leading-2xl font-bold text-black70 whitespace-nowrap"
        >
          {{ headingText }}
        </p>
        <span :class="[$style.upChevron, { [$style.chevronFaded]: hover }]">
          <Icon
            class="inline text-black70"
            :size="18"
            name="i:chevron-up"/>
        </span>
      </div>
      <p
        :class="$style.additionalContent"
        class="text-base leading-normal md:text-3xs md:leading-none lg:text-sm xl:leading-normal xxl:text-base text-black50"
      >
        {{ descriptionText }}
      </p>
      <span :class="$style.additionalContent" class="ml-auto">
        <a :href="readMoreUrl" class="font-bold text-blue-primary text-base">
          {{ readMoreText }}
          <Icon
            class="relative top-[-1px]"
            :size="16"
            name="i:chevron-right-thick" />
        </a>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { extractImageUrl, getInnerText } from "~/scripts/useful-functions"

export default defineComponent({
  name: "ServiceHighlight",

  props: {
    block: {
      required: true,
      type: Object as () => Block
    },
    lazy: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      imageUrl: "" as string | null,
      headingText: "",
      descriptionText: "",
      readMoreText: "",
      readMoreUrl: "",
      hover: false,
      isTouchDevice: false
    }
  },

  created() {
    this.imageUrl = extractImageUrl(this.block.innerBlocks[0].innerHtml)
    this.headingText = getInnerText(this.block.innerBlocks[1].innerHtml)
    this.descriptionText = getInnerText(this.block.innerBlocks[2].innerHtml)
    this.readMoreText = getInnerText(this.block.innerBlocks[3].innerHtml)
    this.readMoreUrl = getInnerText(this.block.innerBlocks[4].innerHtml)
  },

  mounted() {
    // check if touch device
    this.isTouchDevice =
      typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)
  },

  methods: {
    handleEnter() {
      this.hover = true
    },
    handleLeave() {
      this.hover = false
    },
    handleClick() {
      this.hover = !this.hover
    }
  }
})
</script>

<style module>
.cardContent {
  transition: transform 0.3s ease;
  transform: translateY(
    calc(100% - 2rem - 16px)
  ); /** minus line height minus top+bottom padding -- tradeoff is that one line is essential */
  @screen md {
    transform: translateY(calc(100% - 1.5rem - 10px));
  }
  @screen lg {
    transform: translateY(calc(100% - 1.5rem - 20px));
  }
}

.cardContent.hovered {
  transform: translateY(0);
}

.additionalContent {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.cardContent.hovered .additionalContent {
  opacity: 1;
}

.upChevron {
  transition: opacity 0.3s ease;
}

.chevronFaded {
  opacity: 0;
}

.subtleShadow {
  box-shadow:
    inset 0 10px 20px -5px rgba(255, 255, 255, 0.8),
    0 20px 25px -5px rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}
</style>
